
import { useState, useCallback, useEffect } from "react";
import { RichTextEditor } from "../../shared/RichTextEditor";
import { parseJsonSafely, formatKeyPoints } from '../../../lib/formatting-utils';
import { useToast } from "../../../hooks/use-toast";
import { meetingNotesService } from "../../../services/supabase/meeting-notes";
import React from "react";

interface Topic {
  topic: string;
  key_points: string[];
}

interface KeyPointsSectionProps {
  keyPoints: string | Record<string, any> | null | undefined;
  meetingId?: string;
  userId?: string;
}

export function KeyPointsSection({ keyPoints, meetingId, userId }: KeyPointsSectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();
  const [contentReady, setContentReady] = useState(false);
  const [localContent, setLocalContent] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  
  // Initialize content when component mounts or keyPoints change
  useEffect(() => {
    try {
      console.log('Initializing key points');
      setError(null);
      
      // Handle undefined or null keyPoints
      if (keyPoints === undefined || keyPoints === null) {
        const emptyContent = formatKeyPoints({ topics: [], final_takeaways: [] });
        setLocalContent(emptyContent);
        setContentReady(true);
        return;
      }
      
      // Check if keyPoints is already formatted HTML
      if (typeof keyPoints === 'string' && 
          keyPoints.trim().startsWith('<') && 
          keyPoints.includes('</')) {
        setLocalContent(keyPoints);
        setContentReady(true);
        return;
      }
      
      const parsedContent = typeof keyPoints === 'string' ? parseJsonSafely(keyPoints) : keyPoints;
      const formattedContent = formatKeyPoints(parsedContent || { topics: [], final_takeaways: [] });
      
      setLocalContent(formattedContent);
      setContentReady(true);
    } catch (error) {
      console.error('Error initializing key points:', error);
      setError('Failed to load key points content');
      const emptyContent = formatKeyPoints({ topics: [], final_takeaways: [] });
      setLocalContent(emptyContent);
      setContentReady(true);
    }
  }, [keyPoints]);

  const handleEditorChange = useCallback(async (content: string) => {
    console.log('Editor content changed');
    setLocalContent(content);
  }, []);

  const handleBlur = async () => {
    if (!meetingId || !userId) {
      console.error('Missing meetingId or userId');
      toast({
        title: "Error",
        description: "Missing meeting ID or user ID",
        variant: "destructive",
      });
      return;
    }

    try {
      const formattedData = parseEditedContent(localContent);
      console.log('Formatted data for save:', {
        topicsCount: formattedData.topics.length,
        takeawaysCount: formattedData.final_takeaways.length
      });
      
      await meetingNotesService.updateField(
        meetingId,
        userId,
        'key_points',
        JSON.stringify(formattedData)
      );

      toast({
        title: "Success",
        description: "Key points saved successfully",
      });
    } catch (error) {
      console.error('Error saving key points:', error);
      toast({
        title: "Error saving changes",
        description: "Please try again",
        variant: "destructive",
      });
    }
    setIsEditing(false);
  };

  const parseEditedContent = (content: string): { topics: Topic[], final_takeaways: string[] } => {
    try {
      if (!content || content.trim() === '') {
        return { topics: [], final_takeaways: [] };
      }
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(content || '<div></div>', 'text/html');
      const result: { topics: Topic[], final_takeaways: string[] } = {
        topics: [],
        final_takeaways: []
      };
      
      let currentTopic: Topic | null = null;
      let currentPoints: string[] = [];
      let inFinalTakeaways = false;
      
      // Process all h2 headers and their content
      const headings = doc.querySelectorAll('h2');
      headings.forEach((heading, index) => {
        const headingText = heading.textContent || '';
        
        // Save previous section if exists
        if (currentTopic) {
          if (inFinalTakeaways) {
            result.final_takeaways = [...currentPoints];
          } else {
            result.topics.push({
              topic: currentTopic.topic,
              key_points: [...currentPoints]
            });
          }
        }
        
        // Start new section
        inFinalTakeaways = headingText.toLowerCase().includes('takeaway');
        currentTopic = { topic: headingText, key_points: [] };
        currentPoints = [];
        
        // Find content until next heading
        let nextElement = heading.nextElementSibling;
        while (nextElement && nextElement.tagName !== 'H2') {
          if (nextElement.tagName === 'UL') {
            nextElement.querySelectorAll('li').forEach(li => {
              currentPoints.push(li.innerHTML);
            });
          }
          nextElement = nextElement.nextElementSibling;
        }
      });
      
      // Add the final section
      if (currentTopic) {
        if (inFinalTakeaways) {
          result.final_takeaways = [...currentPoints];
        } else {
          result.topics.push({
            topic: currentTopic.topic,
            key_points: [...currentPoints]
          });
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error parsing content:', error);
      return { topics: [], final_takeaways: [] };
    }
  };

  // Error state
  if (error) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Key Points</h3>
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-lg">
          <p>{error}</p>
          <button 
            onClick={() => {
              setError(null);
              setContentReady(false);
              
              // Try to reinitialize with empty content
              const emptyContent = formatKeyPoints({ topics: [], final_takeaways: [] });
              setLocalContent(emptyContent);
              setContentReady(true);
            }} 
            className="text-sm underline mt-2"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Show loading state until content is ready
  if (!contentReady) {
    return <div className="space-y-4">
      <h3 className="text-lg font-semibold">Key Points</h3>
      <div className="animate-pulse p-4 bg-muted/50 rounded-lg h-32"></div>
    </div>;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Key Points</h3>
      {isEditing ? (
        <RichTextEditor
          initialContent={localContent}
          onChange={handleEditorChange}
          onBlur={handleBlur}
          placeholder="Add key points..."
        />
      ) : (
        <div
          onClick={() => setIsEditing(true)}
          className="prose prose-sm max-w-none dark:prose-invert cursor-pointer hover:bg-muted/50 rounded-lg p-4"
          dangerouslySetInnerHTML={{ 
            __html: localContent || "<p>Add key points...</p>"
          }}
        />
      )}
    </div>
  );
}
